import { Component, EventEmitter, Output, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-attach-file-modal',
  templateUrl: './attach-file-modal.component.html',
  styleUrls: ['./attach-file-modal.component.css']
})
export class AttachFileModalComponent {
  @Input() multiple: boolean = true;
  @Output() fileSelected = new EventEmitter<File[]>();

  selectedFiles: File[] = [];
  isDragging = false;
  maxFileSize = 10 * 1024 * 1024; // 10MB
  allowedFileTypes = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private toastr: ToastrService,
    private translateService: TranslateService
  ) {}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(Array.from(input.files));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFiles(Array.from(event.dataTransfer.files));
    }
  }

  private handleFiles(files: File[]): void {
    let filesToProcess = files;
    if (!this.multiple && files.length > 0) {
      filesToProcess = [files[0]];
    }

    for (const file of filesToProcess) {
      // Validate file size
      if (file.size > this.maxFileSize) {
        this.toastr.error(
          this.translateService.instant('ATTACHMENTS.ERROR.FILE_TOO_LARGE') ||
            `File size exceeds 10MB limit (${file.name})`
        );
        continue;
      }

      // Validate file type
      if (!this.allowedFileTypes.includes(file.type)) {
        this.toastr.warning(
          this.translateService.instant('ATTACHMENTS.ERROR.INVALID_FILE_TYPE') ||
            `Invalid file type (${file.name}). Allowed: PDF, Images, Word, Excel`
        );
        continue;
      }

      if (!this.multiple) {
        this.selectedFiles = [file];
      } else {
        // Prevent adding duplicate files
        if (!this.selectedFiles.some(f => f.name === file.name && f.size === file.size)) {
          this.selectedFiles.push(file);
        }
      }
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  onBrowseClick(): void {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    fileInput?.click();
  }

  onSave(): void {
    if (this.selectedFiles.length === 0) {
      this.toastr.warning(
        this.translateService.instant('ATTACHMENTS.ERROR.NO_FILE_SELECTED') ||
          'Please select a file to upload'
      );
      return;
    }

    this.fileSelected.emit(this.selectedFiles);
    this.activeModal.close(this.selectedFiles);
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  getFileIcon(file: File): string {
    if (file.type.startsWith('image/')) return 'bi-file-image';
    if (file.type === 'application/pdf') return 'bi-file-pdf';
    if (file.type.includes('word')) return 'bi-file-word';
    if (file.type.includes('excel') || file.type.includes('spreadsheet')) return 'bi-file-excel';
    return 'bi-file-earmark';
  }
}
